import { ChangeDetectorRef, OnDestroy, Optional, Pipe, PipeTransform } from '@angular/core';

import { Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TimeagoClock, TimeagoFormatter, TimeagoIntl } from 'ngx-timeago';

function coerceBooleanProperty(value: any): boolean {
  return value != null && `${value}` !== 'false';
}

function parseDate(date: any): number {
  if (date?.seconds) {
    return date.seconds * 1000;
  }

  const parsed = new Date(date).valueOf();

  if (!Number.isNaN(parsed)) {
    return parsed;
  }

  const parts = String(date).match(/\d+/g);

  if (!parts || parts.length <= 2) {
    return parsed;
  }

  const [year, month, ...rest] = parts.map((part) => parseInt(part, 10));
  return new Date(Date.UTC(year, month - 1, ...rest)).valueOf();
}

@Pipe({
    name: 'safeTimeago',
    pure: false,
    standalone: false
})
export class SafeTimeagoPipe implements PipeTransform, OnDestroy {
  private intlSubscription?: Subscription;
  private clockSubscription?: Subscription;
  private date?: number;
  private value = '';
  private live = true;
  private stateChanges = new Subject<void>();

  constructor(
    @Optional() intl: TimeagoIntl,
    cd: ChangeDetectorRef,
    private formatter: TimeagoFormatter,
    private clock: TimeagoClock
  ) {
    if (intl) {
      this.intlSubscription = intl.changes.subscribe(() => this.stateChanges.next());
    }

    this.stateChanges.subscribe(() => {
      this.value = this.date ? this.formatter.format(this.date) : '';
      cd.markForCheck();
    });
  }

  transform(date: any, live?: any): string {
    const parsedDate = parseDate(date);
    const parsedLive = live == null ? this.live : coerceBooleanProperty(live);

    if (!parsedDate) {
      this.clearClock();
      this.date = undefined;
      this.value = '';
      return this.value;
    }

    if (this.date === parsedDate && this.live === parsedLive) {
      return this.value;
    }

    this.date = parsedDate;
    this.live = parsedLive;
    this.clearClock();
    this.clockSubscription = this.clock.tick(this.date)
      .pipe(filter(() => this.live))
      .subscribe(() => this.stateChanges.next());
    this.stateChanges.next();

    return this.value;
  }

  ngOnDestroy(): void {
    this.intlSubscription?.unsubscribe();
    this.clearClock();
    this.stateChanges.complete();
  }

  private clearClock(): void {
    this.clockSubscription?.unsubscribe();
    this.clockSubscription = undefined;
  }
}
